import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AnalyzedToken } from '../shared/models/analyzed-token.model';
import { Sense } from '../shared/models/sense.model';
import { EntryRequest } from './interfaces/entry-request.model';
import { Entry, EntryResponse } from './interfaces/entry.model';
import { LexicalEntry } from './interfaces/lexical-entry.model';
import { ReduceResource } from './interfaces/reduce-resource.model';
import { ExportTokenReq, SearchTokenReq } from './interfaces/search-token-req.model';
import { SearchTokenResp } from './interfaces/search-token-resp.model';
import { SearchTokens } from './interfaces/search-tokens.model';
import { MorphologyTrait, SemanticGroupTextualForms, TextualForm } from './interfaces/semantic-group-textual-forms.model';
import { POS } from './store';

/**Classe che raggruppa i servizi per la sezione di ricerca avanzata */
@Injectable({
  providedIn: 'root'
})
export class RicercaService {

  /**
   * Costruttore per la classe RicercaService
   * @param http {HttpClient}
   */
  constructor(
    private http: HttpClient
  ) { }

  /**Metodo che gestisce la chiamata a BE per il recupero dell'albero delle risorse privo di unità logiche */
  getResourceTree(): Observable<ReduceResource> {
    return this.http.get<ReduceResource>('service/resource/root');
  }

  getLexiconPos(): Observable<POS[]> {
    return this.http.get<POS[]>('LexO-backend-beta/service/lexicon/statistics/pos');
  }

  getLexicalSensesList(body: EntryRequest): Observable<EntryResponse> {
    return this.http.post<EntryResponse>(
      'LexO-backend-beta/service/lexicon/data/lexicalSenses',
      body
    );
  }

  getLexoFormsList(body: SearchTokens): Observable<SemanticGroupTextualForms[]> {
    return this.http.post<SemanticGroupTextualForms[]>(
      'LexO-backend-beta/service/lexicon/data/formsBySense',
      body
    );
  }

  getLexoFormsListForQE(body: SearchTokens): Observable<SemanticGroupTextualForms[]> {
    return this.http.post<SemanticGroupTextualForms[]>(
      'LexO-backend-beta/service/queryExpansion/formsBySense',
      body
    );
  }

  getLexoIdFormsList(id: string): Observable<TextualForm[]> {
    return this.http.get<TextualForm[]>('LexO-backend-beta/service/lexicon/data/' + id + '/forms');
  }

  createLexoLexicalEntry(): Observable<LexicalEntry> {
    return this.http.get<LexicalEntry>('LexO-backend-beta/service/lexicon/creation/lexicalEntry');
  }

  getOccurenceFromResource(body: SearchTokenReq): Observable<SearchTokenResp> {
    console.log(body);
    return this.http.post<SearchTokenResp>('service/search/search', body);
  }

  getSenseByUsem(id: string): Observable<Sense> {
    return this.http.get<Sense>('LexO-backend-beta/service/lexicon/data/' + id + '/lexicalSense');
  }

  getSensesByConcept(templateId: string): Observable<Entry[]> {
    return this.http.get<Entry[]>('LexO-backend-beta/service/lexicon/data/' + templateId + '/sensesByConcept');
  }

  getReferencedLinguisticObject(templateIdList: string[]): Observable<Entry[]> {
    return this.http.post<Entry[]>('LexO-backend-beta/service/queryExpansion/referencedLinguisticObject', {
      conceptList: templateIdList
    })
  }

  getReferencedLinguisticObject2(templateIdList: string[], traitList: string[]): Observable<Entry[]> {
    return this.http.post<Entry[]>('LexO-backend-beta/service/queryExpansion/referencedLinguisticObject2', {
      conceptList: templateIdList.length > 0 ? templateIdList : [''],
      traitList: traitList.length > 0 ? traitList : ['']
    })
  }

  getQueryExpansionForms(lexicalEntryList: string[]): Observable<TextualForm[]> {
    return this.http.post<TextualForm[]>('LexO-backend-beta/service/queryExpansion/forms', {
      lexicalEntryList: lexicalEntryList
    });
  }

  applyMorphologicalFilters(queryFormType: string, queryFormName: string, selectedMorphList: { mus: string, morph: any }[], workingForms: SemanticGroupTextualForms[], lexicalSenses: Entry[]): SemanticGroupTextualForms[] {
    if (queryFormType === 'keyword') {
      let tempSgFilter: SemanticGroupTextualForms;
      workingForms.forEach((s, i) => {
        if (s.semanticRelation === '') {
          workingForms[i] = { ...workingForms[i], target: s.target.filter(t => t.label === queryFormName) };
          tempSgFilter = { ...workingForms[i] };
        } else {
          let accumulatore: TextualForm[] = [];
          tempSgFilter.target.forEach(sourceFilter => {
            accumulatore.push(...this.cleanFormsFromMorphology(sourceFilter.morphology, s.target));
          });
          workingForms[i] = { ...workingForms[i], target: [...accumulatore] };
        }
      });
    }
    if (selectedMorphList.length > 0) {
      workingForms.forEach((s, i) => {
        let indexMorph = selectedMorphList.findIndex(m => m.mus === s.lexicalEntryInstanceName);
        if (s.semanticRelation === '' && indexMorph !== -1) {
          workingForms[i] = { ...workingForms[i], target: this.cleanForms(selectedMorphList[indexMorph], s.target) };
        } else if (s.semanticRelation !== '') {
          let parentMus = lexicalSenses.filter(ls => ls.senseInstanceName === s.senseInstanceName)[0].lexicalEntryInstanceName;
          let indexParentMorph = selectedMorphList.findIndex(pm => pm.mus === parentMus);
          if (indexParentMorph !== -1) {
            workingForms[i] = { ...workingForms[i], target: this.cleanForms(selectedMorphList[indexParentMorph], s.target) };
          }
        }
        if (workingForms[i].target.length === 0) {
          workingForms.splice(i, 1);
        }

      })
    }
    return workingForms;
  }

  private cleanForms(morphRules: { mus: string, morph: any }, formList: TextualForm[]): TextualForm[] {
    let resultForms = [...formList];

    for (let key in morphRules.morph) {
      if (morphRules.morph[key] !== null && morphRules.morph[key].length > 0) {
        resultForms = resultForms.filter(r => {
          return r.morphology.findIndex(t => t.trait === key && morphRules.morph[key].includes(t.value)) !== -1;
        });
      }
    }

    return resultForms;
  }

  private cleanFormsFromMorphology(morphs: MorphologyTrait[], targetForms: TextualForm[]): TextualForm[] {
    let resultForms = [...targetForms];
    for (let morph of morphs) {
      if (morph.trait !== 'gender') {
        resultForms = resultForms.filter(r => {
          return r.morphology.findIndex(t => t.trait === morph.trait && morph.value === t.value) !== -1;
        })
      }
    }
    return resultForms;
  }

  getAnalyzedSentence(sentenceId: number): Observable<AnalyzedToken[]> {
    return this.http.get<AnalyzedToken[]>('service/translation/' + sentenceId + '/analysis')
  }

  postExportQueryResults(body: ExportTokenReq) {
    return this.http.post('service/search/export',
      body,
      {
        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }
}
